/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import { useSnackbar } from "notistack";
import * as orgAdmin from "../../api/orgAdmin";
import { formatBytes, formatDate } from "../apps/drive/format";

const TAB_SX = { textTransform: "none", minHeight: 40, fontSize: 13.5 };

/**
 * A single member, WorkDrive-style: Profile (role, danger zone) and Teams
 * (every org team, toggle membership). Replaces a bare inline role picker with
 * somewhere an admin can actually manage what a person can reach.
 */
const AdminMemberDetail = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTab] = useState("profile");
  const [member, setMember] = useState(null);
  const [teams, setTeams] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  const confirmRemove = async () => {
    setRemoving(true);
    setRemoveError(null);
    try {
      await orgAdmin.removeMember(userId);
      enqueueSnackbar(`${displayName} removed from the organisation.`, { variant: "success" });
      navigate("/admin/members");
    } catch (err) {
      setRemoveError(err.message || "Could not remove this member.");
      setRemoving(false);
    }
  };

  const loadMember = useCallback(async () => {
    try {
      setMember(await orgAdmin.getMember(userId));
    } catch (err) {
      setError(err.message || "Could not load this member.");
    }
  }, [userId]);

  const loadTeams = useCallback(async () => {
    try {
      setTeams(await orgAdmin.getMemberTeams(userId));
    } catch (err) {
      setError(err.message || "Could not load teams.");
      setTeams([]);
    }
  }, [userId]);

  useEffect(() => {
    loadMember();
    loadTeams();
  }, [loadMember, loadTeams]);

  const changeRole = async (role) => {
    setBusy(true);
    try {
      const updated = await orgAdmin.setMemberRole(userId, role);
      // Only the role changed; the profile/storage figures are unaffected.
      setMember((prev) => (prev ? { ...prev, role: updated.role ?? role } : prev));
      enqueueSnackbar(`Role updated to ${role}.`, { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.message || "Could not change the role.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const toggleTeam = async (team) => {
    setBusy(true);
    try {
      if (team.is_member) await orgAdmin.removeMemberFromTeam(userId, team.id);
      else await orgAdmin.addMemberToTeam(userId, team.id);
      await loadTeams();
    } catch (err) {
      enqueueSnackbar(err.message || "Could not update team membership.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (error && !member) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (!member) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "accent.main" }} />
      </Box>
    );
  }

  const displayName = member.user_full_name || member.user_email || member.user_id;

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/admin/members")}
        sx={{ color: "text.color", textTransform: "none", mb: 2 }}
      >
        Back to Members
      </Button>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Avatar sx={{ width: 52, height: 52, fontSize: 20, bgcolor: "accent.solid" }}>
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 19, fontWeight: 600 }}>{displayName}</Typography>
            {member.role === "admin" && (
              <Chip size="small" label="Org Admin" sx={{ fontSize: 10.5 }} />
            )}
          </Box>
          <Typography sx={{ fontSize: 13, color: theme.palette.secondaryText.color }}>
            {member.user_email}
          </Typography>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, "& .MuiTabs-indicator": { backgroundColor: "accent.main" } }}
      >
        <Tab value="profile" label="Profile" sx={TAB_SX} />
        <Tab value="teams" label="Team Folders" sx={TAB_SX} />
      </Tabs>

      {tab === "profile" && (
        <Box sx={{ maxWidth: 560, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: 13.5 }}>Account status</Typography>
            <Chip size="small" label={member.user_status || "active"} sx={{ fontSize: 11, textTransform: "capitalize" }} />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: 13.5 }}>Joined organisation</Typography>
            <Typography sx={{ fontSize: 13.5 }}>{formatDate(member.joined_at)}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: 13.5 }}>Files</Typography>
            <Typography sx={{ fontSize: 13.5 }}>{member.file_count}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: 13.5 }}>Storage used</Typography>
            <Typography sx={{ fontSize: 13.5 }}>{formatBytes(Number(member.bytes_used || 0))}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: 13.5 }}>Role</Typography>
            <Select
              size="small"
              value={member.role}
              disabled={busy}
              onChange={(e) => changeRole(e.target.value)}
              sx={{ fontSize: 13, minWidth: 140 }}
            >
              <MenuItem value="member" sx={{ fontSize: 13 }}>Member</MenuItem>
              <MenuItem value="admin" sx={{ fontSize: 13 }}>Admin</MenuItem>
            </Select>
          </Box>

          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.border.color}` }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, mb: 1 }}>Danger zone</Typography>
            <Typography sx={{ fontSize: 12.5, color: theme.palette.secondaryText.color, mb: 1.5 }}>
              Removes this person from the organisation and every team folder in it.
              Their files are not deleted.
            </Typography>
            <Button
              color="error"
              variant="outlined"
              startIcon={<PersonRemoveOutlinedIcon />}
              sx={{ textTransform: "none" }}
              onClick={() => setConfirmingRemove(true)}
            >
              Remove from organisation
            </Button>
          </Box>
        </Box>
      )}

      {tab === "teams" && (
        <Box sx={{ maxWidth: 560 }}>
          {teams === null ? (
            <CircularProgress size={20} sx={{ color: "accent.main" }} />
          ) : teams.length === 0 ? (
            <Typography sx={{ fontSize: 13.5, color: theme.palette.secondaryText.color }}>
              No team folders exist in this organisation yet.
            </Typography>
          ) : (
            teams.map((team, index) => (
              <Box
                key={team.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1.25,
                  borderTop: index === 0 ? "none" : `1px solid ${theme.palette.border.color}`,
                }}
              >
                <Typography sx={{ fontSize: 13.5, flex: 1 }}>{team.team_name}</Typography>
                {team.is_member && (
                  <Chip size="small" label={team.member_role} sx={{ fontSize: 10.5, textTransform: "capitalize" }} />
                )}
                <Tooltip title={team.is_member ? "Remove from team" : "Add to team"}>
                  <span>
                    <IconButton size="small" disabled={busy} onClick={() => toggleTeam(team)}>
                      {team.is_member ? (
                        <PersonRemoveOutlinedIcon fontSize="small" sx={{ color: "error.main" }} />
                      ) : (
                        <GroupAddOutlinedIcon fontSize="small" sx={{ color: "accent.main" }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))
          )}
        </Box>
      )}

      <Dialog open={confirmingRemove} onClose={removing ? undefined : () => setConfirmingRemove(false)} fullWidth maxWidth="xs">
        <DialogTitle>Remove {displayName}?</DialogTitle>
        <DialogContent>
          {removeError && <Alert severity="error" sx={{ mb: 2 }}>{removeError}</Alert>}
          <DialogContentText>
            They will lose access to this organisation and every team folder in it.
            Their files stay exactly where they are — nothing is deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingRemove(false)} disabled={removing} sx={{ color: "text.color", textTransform: "none" }}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmRemove} disabled={removing}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminMemberDetail;
